import React, { useState, useEffect } from 'react';

import Select from '~/components/Select';
import SimplePieType from '~/components/StatisticsCharts/SimplePieType';

import { getRepValueForSelect, getClientValueForSelect } from '~/utils';

import { useAuth } from '~/hooks/auth';

import api from '~/services/api';

import { STATE_OPTIONS } from '../../../constants';

interface IFilters {
  state: string;
  state_refreshed: boolean;
  erp_rep_code: string;
  rep_refreshed: boolean;
  erp_client_code: string;
  year: string;
  refresh: boolean;
  refresh_projects: boolean;
}

const Status: React.FC = () => {
  const { user } = useAuth();
  const [representatives, setRepresentatives] = useState<
    {
      code: string;
      name: string;
    }[]
  >();
  const [clients, setClients] = useState<
    {
      code: string;
      company_name: string;
    }[]
  >();
  const [projects, setProjects] = useState<
    {
      type: string;
      status: string;
    }[]
  >([]);
  const [filters, setFilters] = useState<IFilters>({
    state: 'ALL',
    state_refreshed: true,
    erp_rep_code: user.user_type.type === 'REP' ? user.erp_rep_code : 'ALL',
    rep_refreshed: true,
    erp_client_code: 'ALL',
    year: 'ALL',
    refresh: true,
    refresh_projects: false,
  });
  const [displayProjects, setDisplayProjects] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });
  const [materialProjects, setMaterialProjects] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });
  const [sponsorshipProjects, setSponsorshipProjects] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });
  const [adversityReports, setAdversityReports] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });
  const [folderProjects, setFolderProjects] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });
  const [sampleProjects, setSampleProjects] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });
  const [eventProjects, setEventProjects] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });
  const [rebateProjects, setRebateProjects] = useState({
    pending: 0,
    finished: 0,
    canceled: 0,
  });

  useEffect(() => {
    const refreshFilters = async () => {
      let { erp_rep_code, erp_client_code, rep_refreshed } = filters;

      if (filters.state_refreshed) {
        await api
          .get(`/localrepresentatives/state?state=${filters.state}`)
          .then(response => {
            setRepresentatives([
              {
                code: 'ALL',
                name: '',
              },
              ...response.data,
            ]);

            erp_rep_code = 'ALL';
            rep_refreshed = true;
          });
      }

      if (rep_refreshed) {
        erp_rep_code =
          user.user_type.type === 'REP' ? user.erp_rep_code : erp_rep_code;

        await api
          .get(`/localclients/rep?rep_code=${erp_rep_code}`)
          .then(response => {
            setClients([
              {
                code: 'ALL',
                company_name: '',
              },
              ...response.data,
            ]);

            erp_client_code = 'ALL';
          });
      }

      setFilters({
        ...filters,
        state_refreshed: false,
        erp_rep_code,
        rep_refreshed: false,
        erp_client_code,
        refresh: false,
        refresh_projects: true,
      });
    };

    if (filters.refresh) {
      refreshFilters();
    }
  }, [filters, user]);

  useEffect(() => {
    const loadProjects = async () => {
      await api
        .get(
          `/projects/projectstatistics?` +
            `state=${filters.state}&` +
            `erp_rep_code=${filters.erp_rep_code}&` +
            `erp_client_code=${filters.erp_client_code}&` +
            `status=ALL&` +
            `year=${filters.year}`,
        )
        .then(response => {
          setProjects(response.data);

          setFilters({ ...filters, refresh_projects: false });
        });
    };

    if (filters.refresh_projects) {
      loadProjects();
    }
  }, [filters]);

  useEffect(() => {
    setDisplayProjects({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'DISPLAY',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'DISPLAY',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'DISPLAY',
      ).length,
    });
    setMaterialProjects({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'MATERIAL',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'MATERIAL',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'MATERIAL',
      ).length,
    });
    setSponsorshipProjects({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'PATROCINIO',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'PATROCINIO',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'PATROCINIO',
      ).length,
    });
    setAdversityReports({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'ADVERSIDADE',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'ADVERSIDADE',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'ADVERSIDADE',
      ).length,
    });
    setFolderProjects({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'PASTA',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'PASTA',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'PASTA',
      ).length,
    });
    setSampleProjects({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'AMOSTRA',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'AMOSTRA',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'AMOSTRA',
      ).length,
    });
    setEventProjects({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'EVENTO',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'EVENTO',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'EVENTO',
      ).length,
    });
    setRebateProjects({
      pending: projects.filter(
        project =>
          project.status.toUpperCase() === 'PENDENTE' &&
          project.type === 'REBATE',
      ).length,
      finished: projects.filter(
        project =>
          project.status.toUpperCase() === 'FINALIZADO' &&
          project.type === 'REBATE',
      ).length,
      canceled: projects.filter(
        project =>
          project.status.toUpperCase() === 'CANCELADO' &&
          project.type === 'REBATE',
      ).length,
    });
  }, [projects]);

  const handleFilter = (): React.ReactNode => {
    if (user.user_type.type !== 'REP') {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ width: 250 }}>
            <Select
              label="Estado"
              options={STATE_OPTIONS}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  state: e.value,
                  state_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Representante"
              options={representatives?.map(representative => ({
                value: representative.code,
                label:
                  representative.code === 'ALL'
                    ? 'Todos'
                    : `(${representative.code}) - ${representative.name}`,
              }))}
              value={getRepValueForSelect(
                filters.erp_rep_code,
                representatives,
              )}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_rep_code: e.value,
                  rep_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Cliente"
              options={clients?.map(client => ({
                value: client.code,
                label:
                  client.code === 'ALL'
                    ? 'Todos'
                    : `(${client.code}) - ${client.company_name}`,
              }))}
              value={getClientValueForSelect(filters.erp_client_code, clients)}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_client_code: e.value,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Ano"
              options={[
                { value: 'ALL', label: 'Todos' },
                ...Array.from(
                  { length: new Date().getFullYear() - 2021 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map(y => ({ value: String(y), label: String(y) })),
              ]}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  year: e.value,
                  refresh: true,
                });
              }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ maxWidth: 1080 }}>
      {handleFilter()}

      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}
      >
        <SimplePieType label="Displays" data={displayProjects} />

        <SimplePieType label="Materiais" data={materialProjects} />

        <SimplePieType label="Pagamentos" data={sponsorshipProjects} />

        <SimplePieType label="Problemas" data={adversityReports} />

        <SimplePieType label="Pastas" data={folderProjects} />

        <SimplePieType label="Amostras" data={sampleProjects} />

        <SimplePieType label="Eventos" data={eventProjects} />

        <SimplePieType label="Rebate" data={rebateProjects} />
      </div>
    </div>
  );
};

export default Status;
